import { useContext, useEffect, useState, memo} from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useAlert from '../../../../Alerts/useAlert';
import usePrivileges from '../../../../Auth/usePrivileges';
import { LotDetailContext } from '../../../../Contexts/LotDetailContext';
import SelectFilter from '../../../../DataTable/datatable.selectfilter';
import EditableDatatable from '../../../../DataTable/Editable/datatable.editable';
import EditableDateColumn from '../../../../DataTable/Editable/datatable.editable.datecolumn';
import EditableColumn from '../../../../DataTable/Editable/datatable.editable.inputcolumn';
import EditableSelectColumn from '../../../../DataTable/Editable/datatable.editable.selectcolumn';
import ConfirmationDialog from '../../../../Dialogs/ConfirmationDialog';
import { Changes } from '../../../../Types/Changes';
import { Package } from '../../../../Types/Package';
import { PackageType } from '../../../../Types/PackageType';
import WithComment from '../../../../Types/WithComment';
import { usePackageTypes } from '../../../../Utilities/Hooks';
import { useHttpUtils } from '../../../../Utilities/useHttpUtils';
import PackageNumberColumn from './PackageNumberColumn';
import AddPackagesDialog from './AddPackagesDialog';

interface PackagesSectionProps {
    packagesCanBeEdited: boolean,
}

const PackagesSection = memo(function PackagesSection({ packagesCanBeEdited }: PackagesSectionProps) {
    const { t } = useTranslation();
    const { lotDetail: { summary: { lotNumber, businessUnitCode, grade }, packages }, getLotDetail } = useContext(LotDetailContext);
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { canReview } = usePrivileges(businessUnitCode);
    const packageTypes: PackageType[] = usePackageTypes(null, businessUnitCode, grade, true);
    const [canEditPrivilege, setCanEditPrivilege] = useState(!!canReview && packagesCanBeEdited);

    useEffect(() => {
        setCanEditPrivilege(!!canReview && packagesCanBeEdited);
    }, [canReview, packagesCanBeEdited]);

    const packageTypeOptions = packageTypes.map(x => ({
        value: x.name,
        display: x.alternativeName ? `${x.name} (${x.alternativeName})` : x.name
    }));

    const tableColumns = [
        PackageNumberColumn({ width: "10%" }),
        EditableDateColumn('LotDetails.Packages.PackageDateTime', 'packageDateTimeUtc', canEditPrivilege, { width: "15%" }),
        EditableColumn('LotDetails.Packages.Grade', 'grade', canEditPrivilege, 'equals', SelectFilter),
        EditableColumn('LotDetails.Packages.Status', 'status', canEditPrivilege, 'equals', SelectFilter),
        EditableSelectColumn('LotDetails.Packages.PackageType', 'packageType', packageTypeOptions, canEditPrivilege, 'equals', SelectFilter, { width: "15%" }),
        EditableColumn('LotDetails.Packages.TareWeight', 'tareWeight', canEditPrivilege, 'includesString'),
        EditableColumn('LotDetails.Packages.NetWeight', 'netWeight', canEditPrivilege, 'includesString'),
        EditableColumn('LotDetails.Packages.Scale', 'scale', canEditPrivilege, 'equals', SelectFilter),
        EditableColumn('LotDetails.Packages.RedDot', 'redDotCode', canEditPrivilege, 'equals', SelectFilter),
        EditableColumn('LotDetails.Packages.Notes', 'notes', canEditPrivilege, 'equals', SelectFilter),
    ];

    const initialState = {
        sortBy: [
            {
                id: 'containerNumber',
                desc: false
            }
        ],
        hiddenColumns: packages.every(x => x.notes === lotNumber) ? ["LotDetails.Packages.Notes"] : []
    };

    const [loading, setLoading] = useState(false);
    const saveChanges = async (data: Changes<Package>, comment: string) => {
        let url = `/api/v1/lot/${lotNumber}/packages`;
        let body: WithComment<Changes<Package>> = {
            data: data,
            comment: comment,
        };

        setLoading(true);
        await fetchDataAuthenticated(url, 'PUT', JSON.stringify(body))
            .then(async (response: any) => {
                if (response.ok) {
                    setAlert("success", "Success", `Updated the packages`);
                    getLotDetail();
                } else {
                    let errorMessage = await response.text();
                    console.log("Failed to update the package:" + errorMessage);
                    throw new Error("Failed to update the package.");
                }
            })
            .catch((error: any) => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => setLoading(false));
    };

    const [showClearConfirmationDialog, setShowClearConfirmationDialog] = useState(false);
    const [showAddPackagesDialog, setShowAddPackagesDialog] = useState(false);

    if (packages?.length === 0) {
        return (
            <>{t('CommonResponses.NoData')}</>
        );
    }

    return (
        <EditableDatatable identifier={'containerNumber'} tableColumns={tableColumns} initialTableData={packages} initialState={initialState} saveChanges={saveChanges} canDelete={canEditPrivilege}>
            {(api) => (canEditPrivilege &&
                <>
                    <Button onClick={() => setShowClearConfirmationDialog(true)}>
                        {t<string>("LotDetails.Packages.ClearRedDotCodes")}
                    </Button>
                    <Button onClick={() => setShowAddPackagesDialog(true)} className="ms-2">
                        {t<string>("Application.Controls.Add")}
                    </Button>
                    <AddPackagesDialog
                        showDialog={showAddPackagesDialog}
                        closeDialog={() => setShowAddPackagesDialog(false)}
                        packageTypeOptions={packageTypeOptions}
                        addPackage={api.createData}
                    />
                    <ConfirmationDialog
                        title={t<string>("LotDetails.Packages.ClearRedDotCodes")}
                        showDialog={showClearConfirmationDialog}
                        onOk={() => api.applyToFilteredRows('redDotCode', '', true)}
                        closeDialog={() => setShowClearConfirmationDialog(false)}>
                        <p>{t<string>("LotDetails.Packages.ClearRedDotCodesWarning", { redDotCodes: [...new Set(api.filteredRows.map(r => r.original.redDotCode))].filter(code => code) })}</p>
                    </ConfirmationDialog>
                </>
            )}
        </EditableDatatable>
    );
});

export default PackagesSection;